import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Loader2, XCircle, Ticket, Home } from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function StripeSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setStatus('error');
      setError('No session ID found');
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stripe/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(response.data);
      } else if (response.data.status === 'expired') {
        setStatus('error');
        setError('Payment session expired. Please try again.');
      } else {
        setStatus('pending');
        setPaymentDetails(response.data);
      }
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Failed to verify payment');
    }
  };

  if (status === 'loading') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
          <p className="text-lg text-muted-foreground">Verifying your payment...</p>
        </div>
      </Layout>
    );
  }

  if (status === 'error') {
    return (
      <Layout>
        <div className="max-w-md mx-auto mt-12">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Payment Failed</h1>
              <p className="text-muted-foreground mb-6">{error}</p>
              <div className="flex flex-col gap-3">
                <Button onClick={() => navigate(-1)} variant="outline" className="w-full">
                  Try Again
                </Button>
                <Link to="/">
                  <Button variant="ghost" className="w-full gap-2">
                    <Home className="w-4 h-4" />
                    Back to Home
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-md mx-auto mt-12">
        <Card>
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
            <p className="text-muted-foreground mb-6">
              Your tickets have been purchased successfully.
            </p>
            
            {paymentDetails && (
              <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Amount Paid</span>
                  <span className="font-semibold">
                    {paymentDetails.currency?.toUpperCase()} {paymentDetails.amount?.toLocaleString()}
                  </span>
                </div>
                {paymentDetails.customer_email && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email</span>
                    <span className="text-sm">{paymentDetails.customer_email}</span>
                  </div>
                )}
              </div>
            )}
            
            <p className="text-sm text-muted-foreground mb-6">
              A confirmation email with your tickets has been sent to your email address.
            </p>
            
            <div className="flex flex-col gap-3">
              <Link to="/my-tickets">
                <Button className="w-full gap-2">
                  <Ticket className="w-4 h-4" />
                  View My Tickets
                </Button>
              </Link>
              <Link to="/events">
                <Button variant="outline" className="w-full">
                  Browse More Events
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
